// src/providers/RateLimitProvider.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AuthError } from '../utils/Authentication/authErrorHandler';

interface RateLimitState {
  isRateLimited: boolean;
  retryAfter?: number;
  resetTime?: number;
  violationCount?: number;
  delayLevel?: number;
  description?: string;
  error?: AuthError;
}

interface RateLimitContextType {
  rateLimitState: RateLimitState;
  setRateLimitError: (error: AuthError) => void;
  clearRateLimitError: () => void;
  retryAction: () => void;
}

const RateLimitContext = createContext<RateLimitContextType | undefined>(undefined);

interface RateLimitProviderProps {
  children: ReactNode;
}

export const RateLimitProvider: React.FC<RateLimitProviderProps> = ({ children }) => {
  const [rateLimitState, setRateLimitState] = useState<RateLimitState>({
    isRateLimited: false,
  });

  const setRateLimitError = useCallback((error: AuthError) => {
    const details = error.details || {};
    
    setRateLimitState({
      isRateLimited: true,
      retryAfter: details.retryAfter,
      resetTime: details.resetTime,
      violationCount: details.violationCount,
      delayLevel: details.delayLevel,
      description: details.description,
      error,
    });
  }, []);

  const clearRateLimitError = useCallback(() => {
    setRateLimitState({
      isRateLimited: false,
    });
  }, []);

  const retryAction = useCallback(() => {
    // Clear the rate limit error and allow retry
    clearRateLimitError();
  }, [clearRateLimitError]);

  const value: RateLimitContextType = {
    rateLimitState,
    setRateLimitError,
    clearRateLimitError,
    retryAction,
  };

  return (
    <RateLimitContext.Provider value={value}>
      {children}
    </RateLimitContext.Provider>
  );
};

export const useRateLimit = (): RateLimitContextType => {
  const context = useContext(RateLimitContext);
  if (context === undefined) {
    throw new Error('useRateLimit must be used within a RateLimitProvider');
  }
  return context;
};

export default RateLimitProvider;
